import { useQueries, useQuery } from 'react-query';
import useSeedEntityCache from './useSeedEntityCache';

const SKELETON_ROWS = 500;

/**
 * The payload that names one request. Every source sends the same field set;
 * an axis a source has no concept of arrives undefined, which is identical to
 * omitting it both in the cache key and on the wire - JSON.stringify drops
 * undefined either side.
 */
const buildQueryKey = ({
    per_page = 500,
    order_by,
    ascending,
    week,
    year,
    paginateBy,
    brands,
    categories,
    categoryDescendants,
    hashtags,
    countries,
    withPricePoint = false,
}) => ({
    paginateBy,
    week,
    year,
    order_by,
    ascending,
    country: countries,
    category: categories,
    // The server takes one category predicate; sending both would make the
    // narrower of the two silently win.
    ...(categoryDescendants
        ? { category: null, category_descendants: categoryDescendants }
        : {}),
    brand: brands,
    hashtag: hashtags,
    per_page,
    // Conditional so the callers that never asked keep their query keys.
    ...(withPricePoint ? { withPricePoint: true } : {}),
});

/**
 * Builds one source's paged data hook. `queryName` is the cache namespace, so
 * no two sources may share one.
 */
const createSourceDataHook =
    ({
        queryName,
        queryFn,
        seedEntitiesFor,
        defaults,
        mapRows,
        placeholderOnRestPages = true,
        restRetry,
        skeletonRowIds = true,
    }) =>
    (args) => {
        /* An explicit `undefined` must not wipe a default: these hooks took
           their defaults through destructuring, where it falls back. */
        const withDefaults = { ...defaults };
        Object.entries(args || {}).forEach(([key, value]) => {
            if (value !== undefined) withDefaults[key] = value;
        });
        const {
            enabled = true,
            fetchAllPages = true,
            placeholderData,
        } = withDefaults;
        const commonQueryKey = buildQueryKey(withDefaults);

        const placeholder = placeholderData?.length
            ? () => ({
                  data: placeholderData,
                  total_records: placeholderData?.length || 0,
              })
            : undefined;

        const firstPageQuery = useQuery({
            queryKey: [queryName, { ...commonQueryKey, page: 1 }],
            queryFn,
            enabled,
            staleTime: Infinity,
            placeholderData: placeholder,
        });

        useSeedEntityCache(
            seedEntitiesFor ? firstPageQuery.data?.entities : undefined,
            seedEntitiesFor,
        );

        const lastPage =
            fetchAllPages &&
            firstPageQuery.data &&
            firstPageQuery.data.last_page
                ? firstPageQuery.data.last_page
                : 0;

        const restPageQueries = useQueries(
            new Array(Math.max(lastPage - 1, 0)).fill('').map((_, idx) => ({
                queryKey: [queryName, { ...commonQueryKey, page: idx + 2 }],
                queryFn,
                staleTime: Infinity,
                ...(restRetry === undefined ? {} : { retry: restRetry }),
                ...(placeholderOnRestPages
                    ? { placeholderData: placeholder }
                    : {}),
            })),
        );

        let flatData;

        if (
            firstPageQuery.data &&
            firstPageQuery.data.data &&
            firstPageQuery.data.data.length
        ) {
            flatData = [...firstPageQuery.data.data];
        }

        let hasErroredPages = false;

        restPageQueries.forEach(({ data, isLoading, isError }, queryPage) => {
            if (isError) hasErroredPages = true;
            if (data && data.data && data.data.length) {
                if (!flatData) flatData = [];
                flatData.push(...data.data);
            } else if (isLoading) {
                if (!flatData) flatData = [];
                flatData.push(
                    ...new Array(SKELETON_ROWS).fill('').map((_s, idx) => ({
                        fetchingData: true,
                        fetching: true,
                        ...(skeletonRowIds
                            ? { id: `loading-${queryPage}-${idx}` }
                            : {}),
                    })),
                );
            }
        });

        const isLoading =
            firstPageQuery.isLoading ||
            (firstPageQuery.isSuccess &&
                restPageQueries.length &&
                restPageQueries.some(({ isLoading }) => isLoading));
        const isSuccess =
            firstPageQuery.isSuccess &&
            (!(restPageQueries && restPageQueries.length) ||
                restPageQueries.every(({ isSuccess }) => isSuccess));

        return {
            data: mapRows && flatData ? mapRows(flatData) : flatData,
            isLoading,
            isSuccess,
            isError:
                firstPageQuery.isError ||
                restPageQueries.some(({ isError }) => isError),
            isPlaceholderData: false,
            hasErroredPages,
            areMorePagesLoading:
                firstPageQuery.isSuccess &&
                restPageQueries.some(
                    (q) => !q.isError && (q.isPlaceholderData || q.isFetching),
                ),
            firstPageQuery,
            restPageQueries,
            pages: [firstPageQuery, ...restPageQueries],
        };
    };

export default createSourceDataHook;
